import React, { useState, useMemo, useEffect } from 'react';
import { SportItem, EventSnapshot, Market, Outcome, TournamentItem } from '../../api/types';
import { useTranslation } from '../../i18n';
import { SCHNELLZUGRIFF_ITEMS, TOP_WETTBEWERBE, TOP_SPORTARTEN, ALLE_SPORTARTEN, SPORT_PRIORITIES, TOURNAMENT_ID_MAP } from '../../constants/tipico_menu';
import { COUNTRY_FLAGS, compareCountries, EN_TO_DE_COUNTRIES } from '../../constants/countries';
import { BASE_URL, getTournaments } from '../../api/api';

export const SPORT_EMOJI: Record<string, string> = {
  Soccer: '⚽', Basketball: '🏀', Tennis: '🎾', 'Ice Hockey': '🏒',
  Handball: '🤾', Volleyball: '🏐', Baseball: '⚾', 'American Football': '🏈',
  'Australian Football': '🏉', Boxing: '🥊', Cricket: '🏏', Darts: '🎯', Golf: '⛳',
  Motorsport: '🏎️', Cycling: '🚴', Rugby: '🏉',
  'Table Tennis': '🏓', eSoccer: '🎮', eBasketball: '🎮',
};

export const ALLOWED_SPORTS: Record<string, string> = {
  'American Football': 'American Football',
  'Australian Football': 'Australian Football',
  'Baseball': 'Baseball',
  'Basketball': 'Basketball',
  'Boxing': 'Boxen',
  'Cricket': 'Cricket',
  'Darts': 'Darts',
  'Ice Hockey': 'Eishockey',
  'Soccer': 'Fußball',
  'Golf': 'Golf',
  'Handball': 'Handball',
  'Motorsport': 'Motorsport',
  'Cycling': 'Radsport',
  'Rugby': 'Rugby',
  'Snooker': 'Snooker',
  'Tennis': 'Tennis',
  'Table Tennis': 'Tischtennis',
  'Volleyball': 'Volleyball',
  'Winter Sports': 'Wintersport',
  'eSoccer': 'eFootball',
};

export const TOP_COMPETITIONS = [
  { name: 'Champions League', flag: '🏆' },
  { name: 'Premier League', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { name: 'Bundesliga', flag: '🇩🇪' },
  { name: 'La Liga', flag: '🇪🇸' },
  { name: 'Serie A', flag: '🇮🇹' },
  { name: 'Ligue 1', flag: '🇫🇷' },
];

export interface DesktopLeftSidebarProps {
  activeSport: string;
  setActiveSport: (sport: string) => void;
  quickFilter: string | null;
  setQuickFilter: (filter: string | null) => void;
  timeFilter: 'all' | 'heute' | '3std' | '48std';
  setTimeFilter: (filter: 'all' | 'heute' | '3std' | '48std') => void;
  expandedTopSport: string | null;
  setExpandedTopSport: (sport: string | null) => void;
  expandedAlleSport: string | null;
  setExpandedAlleSport: (sport: string | null) => void;
  expandedCategory: string | null;
  setExpandedCategory: (cat: string | null) => void;
  showAllCountries: boolean;
  setShowAllCountries: (show: boolean) => void;
  selectedTournaments: Set<string>;
  setSelectedTournaments: React.Dispatch<React.SetStateAction<Set<string>>>;
  fetchedTournaments: TournamentItem[];
  toggleTournament: (t: string) => void;
  toggleCountry: (tournaments: Record<string, any>) => void;
  allEvents?: EventSnapshot[];
  onAddBet?: (ev: EventSnapshot, market: Market, outcome: Outcome, tipp: string) => void;
  isSelected?: (eventId: string, marketId: string, outcomeId: string) => boolean;
  onEventClick?: (ev: EventSnapshot) => void;
  onNavigate?: (id: string) => void;
  onOpenSearchModal?: () => void;
}

export const DesktopLeftSidebar: React.FC<DesktopLeftSidebarProps> = ({
  activeSport, setActiveSport, quickFilter, setQuickFilter,
  timeFilter, setTimeFilter, expandedTopSport, setExpandedTopSport,
  expandedAlleSport, setExpandedAlleSport,
  expandedCategory, setExpandedCategory, showAllCountries, setShowAllCountries,
  selectedTournaments, setSelectedTournaments, fetchedTournaments, toggleTournament, toggleCountry,
  allEvents = [], onAddBet, isSelected, onEventClick, onNavigate, onOpenSearchModal
}) => {
  const { t } = useTranslation();
  const topTournaments168 = useMemo(() => {
    return fetchedTournaments;
  }, [fetchedTournaments]);

  const { fetchedSportsTree, fetchedSports } = useMemo(() => {
    const newSportsTree: Record<string, Record<string, Record<string, any>>> = {};
    const sportCountMap: Record<string, number> = {};
    const newSports: SportItem[] = [];

    fetchedTournaments.forEach(t => {
      if (t.event_count > 0 && t.sport_name && t.category_name) {
        if (!newSportsTree[t.sport_name]) newSportsTree[t.sport_name] = {};
        if (!newSportsTree[t.sport_name][t.category_name]) newSportsTree[t.sport_name][t.category_name] = {};
        newSportsTree[t.sport_name][t.category_name][t.tournament_name] = { id: t.tournament_id, count: t.event_count };
        sportCountMap[t.sport_name] = (sportCountMap[t.sport_name] || 0) + t.event_count;
      }
    });

    Object.keys(sportCountMap).forEach(sportName => {
      const item = fetchedTournaments.find(x => x.sport_name === sportName);
      if (item) {
        newSports.push({
          sport_name: sportName,
          sport_id: item.sport_id || '',
          event_count: sportCountMap[sportName]
        });
      }
    });

    return { fetchedSportsTree: newSportsTree, fetchedSports: newSports };
  }, [fetchedTournaments]);

  // loadedTrees and loadingSports removed since sportsTree now contains all data

  const formatOddsSidebar = (val: string | number): string => {
    const n = parseFloat(String(val));
    if (isNaN(n) || n <= 1) return '-';
    return n.toFixed(2).replace('.', ',');
  };

  const find1x2Sidebar = (markets: Market[] | undefined) => {
    const m = markets?.find((mk) => mk.market_id === '1' && mk.status !== -1);
    if (!m) return null;
    const home = m.outcomes.find((o: Outcome) => o.outcome_id === '1');
    const draw = m.outcomes.find((o: Outcome) => o.outcome_id === '2' || o.name.toLowerCase() === 'draw' || o.name.toLowerCase() === 'x');
    const away = m.outcomes.find((o: Outcome) => o.outcome_id === '3');
    if (home && draw && away) return { home, draw, away, market: m };
    return null;
  };



  const handleSportExpand = (sportName: string, sportId: string, type: 'top' | 'all') => {
    const isCurrentlyExpanded = type === 'top' ? expandedTopSport === sportName : expandedAlleSport === sportName;
    setShowAllCountries(false);
    if (type === 'top') {
      setExpandedTopSport(isCurrentlyExpanded ? null : sportName);
    } else {
      setExpandedAlleSport(isCurrentlyExpanded ? null : sportName);
    }
    if (activeSport !== sportName) {
      setActiveSport(sportName);
      setSelectedTournaments(new Set());
    }
  };

  const isTournamentSelected = (tName: string, val: any) => {
    if (selectedTournaments.has(tName)) return true;
    if (val && typeof val === 'object' && 'id' in val && selectedTournaments.has(val.id)) return true;
    return false;
  };

  const getTournamentDisplayCount = (val: any): number => {
    if (typeof val === 'number') return val;
    if (val && typeof val === 'object' && 'count' in val) return val.count;
    return 0;
  };

  const handleTournamentClick = (tName: string, val: any) => {
    const key = (val && typeof val === 'object' && 'id' in val) ? val.id : tName;
    toggleTournament(key);
  };

  const topSportsDynamic = useMemo(() => {
    const TOP_SPORTS_ORDER: Record<string, number> = {
      'Soccer': 1,
      'Basketball': 2,
      'Tennis': 3,
      'Ice Hockey': 4,
      'Volleyball': 5,
      'Handball': 6
    };
    return [...fetchedSports]
      .filter(s => s.sport_name in TOP_SPORTS_ORDER)
      .sort((a, b) => TOP_SPORTS_ORDER[a.sport_name] - TOP_SPORTS_ORDER[b.sport_name]);
  }, [fetchedSports]);

  const allSportsDynamic = useMemo(() => {
    const TOP_SPORTS_ORDER: Record<string, number> = {
      'Soccer': 1,
      'Basketball': 2,
      'Tennis': 3,
      'Ice Hockey': 4,
      'Volleyball': 5,
      'Handball': 6
    };
    return [...fetchedSports]
      .filter(s => !(s.sport_name in TOP_SPORTS_ORDER))
      .sort((a, b) => {
        const nameA = ALLOWED_SPORTS[a.sport_name] || a.sport_name;
        const nameB = ALLOWED_SPORTS[b.sport_name] || b.sport_name;
        return nameA.localeCompare(nameB, 'de');
      });
  }, [fetchedSports]);

  const renderSportItem = (s: SportItem, isLast: boolean, type: 'top' | 'all') => {
    const isSportExpanded = type === 'top' ? expandedTopSport === s.sport_name : expandedAlleSport === s.sport_name;
    const deName = ALLOWED_SPORTS[s.sport_name] || s.sport_name;
    const countries = fetchedSportsTree[s.sport_name] || {};

    return (
      <div key={s.sport_id} className={`flex flex-col ${!isLast ? 'border-b border-[#e6e7e8]' : ''}`}>
        <button
          onClick={() => handleSportExpand(s.sport_name, s.sport_id, type)}
          className={`flex items-center min-h-[40px] px-3 text-left hover:bg-[#f7f7f7] transition-colors ${activeSport === s.sport_name ? 'bg-[#f7f7f7]' : ''}`}
        >
          <span className="w-[24px] flex-shrink-0 flex items-center justify-center text-[18px] mr-2">
            {SPORT_EMOJI[s.sport_name] || '🏅'}
          </span>
          <span className={`text-[15px] flex-1 truncate ${activeSport === s.sport_name ? 'text-[#D41534] font-bold' : 'text-[#212121] font-normal'}`}>
            {deName}
          </span>
          <svg className={`w-[18px] h-[18px] text-[#999999] transition-transform ${isSportExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isSportExpanded && (
          <div className="bg-[#f7f7f7] flex flex-col">
            {Object.keys(countries).length === 0 && (
              <div className="flex justify-center items-center py-4 text-[12px] text-[#727272]">
                <span>Keine Turniere gefunden</span>
              </div>
            )}
            {(() => {
              const sortedCountries = Object.entries(countries).sort(([cA], [cB]) => {
                return compareCountries(cA, cB, deName);
              });
              const topCountries = showAllCountries ? sortedCountries : sortedCountries.slice(0, 8);
              const remainingCount = sortedCountries.length - 8;

              return (
                <>
                  {topCountries.map(([countryName, tournaments]) => {
                    const isCountryExpanded = expandedCategory === countryName;
                    const deCountryName = EN_TO_DE_COUNTRIES[countryName] || countryName;
                    const flag = COUNTRY_FLAGS[deCountryName] || '🏳️';
                    const allT = Object.keys(tournaments);
                    const selectedCount = allT.filter(t => isTournamentSelected(t, tournaments[t])).length;
                    const allChecked = allT.length > 0 && selectedCount === allT.length;
                    const partialChecked = selectedCount > 0 && !allChecked;

                    return (
                      <div key={countryName} className="flex flex-col border-t border-[#e6e7e8]">
                        <div 
                          className="flex items-center min-h-[40px] px-3 hover:bg-[#ebebeb] transition-colors gap-2 cursor-pointer" 
                          onClick={() => setExpandedCategory(isCountryExpanded ? null : countryName)}
                        >
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              toggleCountry(tournaments); 
                            }}
                            className={`w-[16px] h-[16px] flex-shrink-0 rounded-[2px] border flex items-center justify-center transition-colors ${
                              allChecked 
                                ? 'bg-[#D41534] border-[#D41534]' 
                                : partialChecked 
                                  ? 'bg-white border-[#D41534]' 
                                  : 'bg-white border-[#999999] hover:border-[#D41534]'
                            }`}
                          >
                            {allChecked && (
                              <svg className="w-[10px] h-[10px] text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            {partialChecked && <div className="w-[8px] h-[8px] bg-[#D41534] rounded-[1px]" />}
                          </button>
                          <span className="text-[14px] w-[20px] text-center flex-shrink-0">{flag}</span>
                          <span className={`text-[13px] flex-1 truncate ${isCountryExpanded ? 'font-bold text-[#212121]' : 'font-normal text-[#454545]'}`}>
                            {deCountryName}
                          </span>
                          {selectedCount > 0 && <span className="text-[13px] font-bold text-[#D41534]">{selectedCount}</span>}
                          <svg className={`w-[18px] h-[18px] text-[#999999] transition-transform ${isCountryExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>

                        {isCountryExpanded && (
                          <div className="flex flex-col bg-white">
                            {Object.entries(tournaments).sort().map(([tName, val]) => {
                              const isChecked = isTournamentSelected(tName, val);
                              const count = getTournamentDisplayCount(val);
                              return (
                                <button
                                  key={tName}
                                  onClick={() => handleTournamentClick(tName, val)}
                                  className="min-h-[40px] pl-[34px] pr-3 text-left hover:bg-[#f7f7f7] transition-colors flex items-center gap-2 group border-t border-[#f0f0f0]"
                                >
                                  <div className={`w-[16px] h-[16px] flex-shrink-0 rounded-[2px] border flex items-center justify-center transition-colors ${
                                    isChecked ? 'bg-[#D41534] border-[#D41534]' : 'bg-white border-[#999999] group-hover:border-[#D41534]'
                                  }`}>
                                    {isChecked && (
                                      <svg className="w-[10px] h-[10px] text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                  <span className={`text-[12px] flex-1 truncate ${isChecked ? 'text-[#D41534] font-bold' : 'text-[#454545] font-normal group-hover:text-[#212121]'}`}>
                                    {tName}
                                  </span>
                                  <span className="text-[12px] text-[#999999]">{count}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!showAllCountries && remainingCount > 0 && (
                    <button
                      onClick={() => setShowAllCountries(true)}
                      className="min-h-[40px] px-3 text-[13px] font-bold text-[#727272] hover:bg-[#ebebeb] transition-colors flex items-center justify-between border-t border-[#e6e7e8] hover:text-[#212121]"
                    >
                      <span className="ml-[34px]">Weitere {remainingCount} anzeigen</span>
                      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                  {showAllCountries && remainingCount > 0 && (
                    <button
                      onClick={() => setShowAllCountries(false)}
                      className="min-h-[40px] px-3 text-[13px] font-bold text-[#727272] hover:bg-[#ebebeb] transition-colors flex items-center justify-between border-t border-[#e6e7e8] hover:text-[#212121]"
                    >
                      <span className="ml-[34px]">Weniger anzeigen</span>
                      <svg className="w-[18px] h-[18px] rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };



  return (
    <aside className="w-[210px] flex-shrink-0 z-[60] self-stretch hidden xl:block">
      <div className="flex flex-col pb-10 gap-4">
        
        {/* Search */}
        <div>
          <button
            onClick={onOpenSearchModal}
            className="w-full relative bg-white dark:bg-[#252525] rounded-[4px] shadow-[0_1px_3px_0_rgba(0,0,0,0.14)] flex items-center h-[40px] border border-transparent hover:border-[#D41534] transition-colors cursor-text group text-left"
          >
            <div className="w-[36px] h-full flex items-center justify-center text-[#999999] group-hover:text-[#D41534] transition-colors">
              <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <span className="text-[13px] text-[#999999]">Team, Event oder Wettbewerb</span>
          </button>
        </div>

        {/* Categories / Quick Access */}
            {/* Schnellzugriff */}
            <div>
              <h3 className="text-[11px] font-bold text-[#727272] uppercase tracking-[0.5px] px-2 mb-2">{t('sidebar.quickAccess')}</h3>
              <div className="flex flex-col bg-white rounded-[4px] shadow-[0_1px_3px_0_rgba(0,0,0,0.14)]">

                {SCHNELLZUGRIFF_ITEMS.map((item, idx, arr) => (
                  <button key={item.id} onClick={() => {
                    if (onNavigate) {
                      onNavigate(item.id.startsWith('category:') ? ('category/' + encodeURIComponent(item.id.split(':')[1])) : item.id);
                    } else {
                      if (item.id.startsWith('category:')) setActiveSport('Soccer');
                      setQuickFilter(item.id);
                    }
                  }} className={`flex items-center min-h-[40px] px-3 text-left hover:bg-[#f7f7f7] transition-colors ${idx !== arr.length - 1 ? 'border-b border-[#e6e7e8]' : 'last:rounded-b-[4px]'} ${quickFilter === item.id ? 'bg-[#f7f7f7]' : ''}`}>
                    <span className="w-[24px] flex-shrink-0 flex items-center justify-center text-[18px] mr-2">{item.icon}</span>
                    <span className={`text-[13px] flex-1 truncate ${quickFilter === item.id ? 'text-[#D41534] font-bold' : 'text-[#212121] font-normal'}`}>{item.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Top Competitions */}
            <div>
              <h3 className="text-[11px] font-bold text-[#727272] uppercase tracking-[0.5px] px-2 mb-2">{t('sidebar.topCompetitions')}</h3>
              <div className="flex flex-col bg-white rounded-[4px] shadow-[0_1px_3px_0_rgba(0,0,0,0.14)]">
                {(() => {
                  const activeTopWettbewerbe = [...topTournaments168]
                    .filter(t => t.event_count > 0 && t.sport_name === 'Soccer')
                    .sort((a, b) => b.event_count - a.event_count)
                    .slice(0, 5)
                    .map(t => {
                      const deCountry = t.category_name ? (EN_TO_DE_COUNTRIES[t.category_name] || t.category_name) : '';
                      let flag = '⚽';
                      if (t.tournament_name?.includes('Champions League') || t.tournament_name?.includes('Europa League')) {
                        flag = '🏆';
                      } else if (deCountry) {
                        flag = COUNTRY_FLAGS[deCountry] || '⚽';
                      }
                      
                      return {
                        id: `tournament:${t.tournament_name}`,
                        name: t.tournament_name,
                        icon: flag
                      };
                    });

                  return (
                    <>
                      {activeTopWettbewerbe.map((comp, idx) => {
                        const isLast = idx === activeTopWettbewerbe.length - 1;
                        return (
                          <button key={comp.id} onClick={() => {
                            setActiveSport('Soccer');
                            if (onNavigate) {
                              onNavigate('competition/' + encodeURIComponent(comp.id.substring('tournament:'.length)));
                            } else {
                              setQuickFilter(comp.id);
                            }
                          }} className={`flex items-center min-h-[40px] px-3 text-left hover:bg-[#f7f7f7] transition-colors ${idx === 0 ? 'first:rounded-t-[4px]' : ''} ${!isLast ? 'border-b border-[#e6e7e8]' : 'last:rounded-b-[4px]'} ${quickFilter === comp.id ? 'bg-[#f7f7f7]' : ''}`}>
                            <span className="w-[24px] flex-shrink-0 flex items-center justify-center text-[18px] mr-2">{comp.icon}</span>
                            <span className={`text-[13px] flex-1 truncate ${quickFilter === comp.id ? 'text-[#D41534] font-bold' : 'text-[#212121] font-normal'}`}>{comp.name}</span>
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>


            {/* Zeit-Filter (Time Filter) */}
            <div className="bg-white rounded-[4px] shadow-[0_1px_3px_0_rgba(0,0,0,0.14)] flex flex-wrap border border-[#e6e7e8]">
              {[
                { id: 'all', label: t('section.viewAll') },
                { id: 'heute', label: t('time.today') },
                { id: '3std', label: `3 ${t('time.hoursShort')}` },
                { id: '48std', label: `48 ${t('time.hoursShort')}` }
              ].map((tf, idx, arr) => (
                <button
                  key={tf.id}
                  onClick={() => setTimeFilter(tf.id as any)}
                  className={`flex-1 h-[40px] text-[11px] font-bold text-center transition-colors flex items-center justify-center ${timeFilter === tf.id
                    ? 'bg-[#D41534] text-white'
                    : 'bg-[#f7f7f7] text-[#212121] hover:bg-[#e6e7e8]'
                    } ${idx !== arr.length - 1 ? 'border-r border-[#e6e7e8]' : ''} ${idx === 0 ? 'rounded-l-[4px]' : ''} ${idx === arr.length - 1 ? 'rounded-r-[4px]' : ''}`}
                >
                  {tf.label}
                </button>
              ))}
            </div>

            {/* Top-Sportarten */}
            <div>
              <h3 className="text-[11px] font-bold text-[#727272] uppercase tracking-[0.5px] px-2 mb-2">{t('sidebar.topSports')}</h3>
              <div className="flex flex-col bg-white rounded-[4px] shadow-[0_1px_3px_0_rgba(0,0,0,0.14)] overflow-hidden">
                {topSportsDynamic.map((s, idx) => renderSportItem(s, idx === topSportsDynamic.length - 1, 'top'))}
              </div>
            </div>

            {/* Alle Sportarten */}
            <div>
              <h3 className="text-[11px] font-bold text-[#727272] uppercase tracking-[0.5px] px-2 mb-2">{t('sidebar.allSports')}</h3>
              <div className="flex flex-col bg-white rounded-[4px] shadow-[0_1px_3px_0_rgba(0,0,0,0.14)] overflow-hidden">
                {allSportsDynamic.map((s, idx) => renderSportItem(s, idx === allSportsDynamic.length - 1, 'all'))}
              </div>
            </div>
      </div>
    </aside>
  );
}